import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  doc, 
  deleteDoc
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/components/AuthProvider';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  User, 
  MapPin, 
  Phone, 
  Mail, 
  Calendar, 
  CreditCard, 
  Trash2, 
  FileText, 
  CheckCircle,
  AlertCircle,
  Clock,
  Building2
} from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { getCustomerInvoices, getCustomerPaymentMethods } from '@/lib/api';
import { showNotification } from '@/lib/generalNotifications';
import type { Customer, PaymentMethod } from '@/types/firebase';

interface CustomerProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: Customer | null;
}

// Helper function to safely format dates that might be Timestamps
const formatSafeDate = (date: any) => {
  if (!date) return 'N/A';
  if (date instanceof Date) return formatDate(date);
  if (date.toDate && typeof date.toDate === 'function') return formatDate(date.toDate());
  return 'N/A';
};

// Helper function to get card brand details
const getCardBrandDetails = (cardType: string | undefined) => {
  const type = cardType?.toLowerCase() || '';
  
  if (type.includes('visa')) {
    return { name: 'Visa', color: 'text-blue-600', bgColor: 'bg-blue-100' };
  } else if (type.includes('mastercard')) {
    return { name: 'Mastercard', color: 'text-orange-600', bgColor: 'bg-orange-100' };
  } else if (type.includes('amex') || type.includes('american')) {
    return { name: 'American Express', color: 'text-blue-500', bgColor: 'bg-blue-100' };
  } else if (type.includes('discover')) {
    return { name: 'Discover', color: 'text-orange-500', bgColor: 'bg-orange-100' };
  } else {
    return { name: type || 'Card', color: 'text-gray-600', bgColor: 'bg-gray-100' };
  }
};

// Helper function to format expiry date
const formatExpiry = (month?: string, year?: string) => {
  if (!month || !year) return 'N/A';
  return `${month.padStart(2, '0')}/${year}`;
};

// Helper function to get invoice status badge
const getInvoiceStatusBadge = (status: string) => {
  switch (status) {
    case 'paid':
      return <Badge className="bg-green-100 text-green-800">Paid</Badge>;
    case 'overdue':
      return <Badge className="bg-red-100 text-red-800">Overdue</Badge>;
    case 'sent':
      return <Badge className="bg-blue-100 text-blue-800">Sent</Badge>;
    case 'draft':
      return <Badge className="bg-gray-100 text-gray-800">Draft</Badge>;
    default:
      return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
  }
};

const CustomerProfileModal = React.memo(({ open, onOpenChange, customer }: CustomerProfileModalProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState<PaymentMethod | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch customer invoices
  const { data: customerInvoices = [], isLoading: isLoadingInvoices } = useQuery({
    queryKey: ['customerInvoices', customer?.id, user?.companyId],
    queryFn: async () => {
      if (!customer?.id || !user?.companyId) return [];
      try {
        const invoices = await getCustomerInvoices(customer.id, user.companyId);
        return invoices || [];
      } catch (error) {
        console.error('Error fetching customer invoices:', error);
        return [];
      }
    },
    enabled: !!customer?.id && !!user?.companyId && open,
  });

  // Fetch customer payment methods
  const { data: paymentMethods = [], isLoading: isLoadingPaymentMethods } = useQuery({
    queryKey: ['customerPaymentMethods', customer?.id, user?.companyId],
    queryFn: async () => {
      if (!customer?.id || !user?.companyId) {
        return [];
      }
      
      
      try {
        const methods = await getCustomerPaymentMethods(customer.id, user.companyId);
        return methods || [];
      } catch (error) {
        console.error('Error fetching customer payment methods:', error);
        return [];
      }
    },
    enabled: !!customer?.id && !!user?.companyId && open,
  });

  // Delete payment method mutation
  const deletePaymentMethodMutation = useMutation({
    mutationFn: async (paymentMethodId: string) => {
      await deleteDoc(doc(db, 'paymentMethods', paymentMethodId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerPaymentMethods'] });
      showNotification({
        title: 'Payment Method Deleted',
        message: 'Payment method was successfully deleted',
        type: 'success'
      });
      setShowDeleteConfirm(false);
      setPaymentMethodToDelete(null);
    },
    onError: (error) => {
      showNotification({
        title: 'Error',
        message: `Failed to delete payment method: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error'
      });
    }
  });

  // Calculate invoice metrics
  const invoiceMetrics = React.useMemo(() => {
    if (!customerInvoices.length) {
      return {
        total: 0,
        totalAmount: 0,
        paid: 0,
        paidAmount: 0,
        pending: 0,
        pendingAmount: 0,
        overdue: 0,
        overdueAmount: 0
      };
    }

    // Single pass through invoices for better performance
    const stats = customerInvoices.reduce((acc, inv) => {
      const amount = inv?.total || 0;
      acc.totalAmount += amount;
      
      if (inv?.status === 'paid') {
        acc.paid++;
        acc.paidAmount += amount;
      } else if (['sent', 'draft'].includes(inv?.status || '')) {
        acc.pending++;
        acc.pendingAmount += amount;
      } else if (inv?.status === 'overdue') {
        acc.overdue++;
        acc.overdueAmount += amount;
      }
      
      return acc;
    }, {
      total: customerInvoices.length,
      totalAmount: 0,
      paid: 0,
      paidAmount: 0,
      pending: 0,
      pendingAmount: 0,
      overdue: 0,
      overdueAmount: 0
    });
    
    return stats;
  }, [customerInvoices]);

  const handleDeletePaymentMethod = (paymentMethod: PaymentMethod) => {
    setPaymentMethodToDelete(paymentMethod);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePaymentMethod = () => {
    if (paymentMethodToDelete) {
      deletePaymentMethodMutation.mutate(paymentMethodToDelete.id);
    }
  };

  if (!customer) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-semibold text-lg">
                  {`${customer.firstName.charAt(0)}${customer.lastName.charAt(0)}`}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-semibold">
                  {customer.companyName || `${customer.firstName} ${customer.lastName}`}
                </h2>
                <p className="text-sm text-gray-500">Customer Profile</p>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Invoice Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="border-blue-100 bg-blue-50">
                <CardContent className="p-4 text-center">
                  <FileText className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                  <h3 className="text-sm font-medium text-gray-500">Total Invoices</h3>
                  <p className="text-2xl font-bold">{invoiceMetrics.total}</p>
                  <p className="text-sm font-medium text-blue-600">
                    {formatCurrency(invoiceMetrics.totalAmount)}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-green-100 bg-green-50">
                <CardContent className="p-4 text-center">
                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <h3 className="text-sm font-medium text-gray-500">Paid</h3>
                  <p className="text-2xl font-bold">{invoiceMetrics.paid}</p>
                  <p className="text-sm font-medium text-green-600">
                    {formatCurrency(invoiceMetrics.paidAmount)}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-yellow-100 bg-yellow-50">
                <CardContent className="p-4 text-center">
                  <Clock className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                  <h3 className="text-sm font-medium text-gray-500">Pending</h3>
                  <p className="text-2xl font-bold">{invoiceMetrics.pending}</p>
                  <p className="text-sm font-medium text-yellow-600">
                    {formatCurrency(invoiceMetrics.pendingAmount)}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-red-100 bg-red-50">
                <CardContent className="p-4 text-center">
                  <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                  <h3 className="text-sm font-medium text-gray-500">Overdue</h3>
                  <p className="text-2xl font-bold">{invoiceMetrics.overdue}</p>
                  <p className="text-sm font-medium text-red-600">
                    {formatCurrency(invoiceMetrics.overdueAmount)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Tabs for different sections */}
            <Tabs defaultValue="demographics" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="demographics">Demographics</TabsTrigger>
                <TabsTrigger value="invoices">Invoices</TabsTrigger>
                <TabsTrigger value="payment-methods">Payment Methods</TabsTrigger>
              </TabsList>

              {/* Demographics Tab */}
              <TabsContent value="demographics" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Customer Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-1">
                            <Building2 className="h-4 w-4" />
                            Company
                          </h4>
                          <p className="font-medium">{customer.companyName || 'N/A'}</p>
                        </div>
                        
                        <div>
                          <h4 className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-1">
                            <User className="h-4 w-4" />
                            Contact Person
                          </h4>
                          <p className="font-medium">{customer.firstName} {customer.lastName}</p>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Customer Since
                          </h4>
                          <p className="font-medium">
                            {customer.createdAt ? formatSafeDate(customer.createdAt) : 'N/A'}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-1">
                            <Mail className="h-4 w-4" />
                            Email
                          </h4>
                          <p className="font-medium">{customer.email}</p>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-1">
                            <Phone className="h-4 w-4" />
                            Phone
                          </h4>
                          <p className="font-medium">{customer.phone || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            Address
                          </h4>
                          <div className="font-medium">
                            {customer.street && <p>{customer.street}</p>}
                            {(customer.city || customer.state || customer.zip) && (
                              <p>
                                {customer.city}{customer.city && customer.state ? ', ' : ''}
                                {customer.state} {customer.zip}
                              </p>
                            )}
                            {customer.country && <p>{customer.country}</p>}
                          </div>
                        </div>

                        {customer.notes && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-500 mb-1">Notes</h4>
                            <p className="font-medium text-sm">{customer.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Invoices Tab */}
              <TabsContent value="invoices" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Invoice History ({customerInvoices.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isLoadingInvoices ? (
                      <div className="py-8 text-center">Loading invoices...</div>
                    ) : customerInvoices.length > 0 ? (
                      <div className="space-y-3">
                        {customerInvoices.map((invoice) => (
                          <div 
                            key={invoice.id} 
                            className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <FileText className="h-5 w-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium">{invoice.invoiceNumber}</p>
                                <p className="text-sm text-gray-500">
                                  {formatSafeDate(invoice.date)} • Due: {formatSafeDate(invoice.dueDate)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="font-medium">{formatCurrency(invoice.total || 0)}</p>
                                {getInvoiceStatusBadge(invoice.status)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-8 text-center text-gray-500">
                        No invoices found for this customer
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Payment Methods Tab */}
              <TabsContent value="payment-methods" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Saved Payment Methods ({paymentMethods.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isLoadingPaymentMethods ? (
                      <div className="py-8 text-center">Loading payment methods...</div>
                    ) : paymentMethods.length > 0 ? (
                      <div className="space-y-3">
                        {paymentMethods.map((paymentMethod) => {
                          const cardDetails = getCardBrandDetails(paymentMethod.cardType);
                          const provider = (paymentMethod as any).provider as ('nmi'|'stripe'|'payarc'|undefined);
                          const providerBadge = provider ? provider.toUpperCase() : 'NMI';
                          const providerColor = provider === 'stripe' ? 'bg-purple-100 text-purple-700' : provider === 'payarc' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700';
                          return (
                            <div 
                              key={paymentMethod.id} 
                              className={`relative flex items-center justify-between p-4 rounded-lg border ${
                                paymentMethod.isDefault ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
                              }`}
                            >
                              {paymentMethod.isDefault && (
                                <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                                  Default
                                </div>
                              )}
                              
                              <div className="flex items-center gap-4">
                                <div className={`p-3 rounded-full ${cardDetails.bgColor}`}>
                                  <CreditCard className={`h-5 w-5 ${cardDetails.color}`} />
                                </div>
                                <div>
                                  <div className="font-medium">
                                    {paymentMethod.type === 'ach' ? (
                                      <>
                                        {paymentMethod.accountType || 'Bank Account'} •••• {paymentMethod.last4}
                                      </>
                                    ) : (
                                      <>
                                        {cardDetails.name} •••• {paymentMethod.last4}
                                      </>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {paymentMethod.type === 'ach' ? (
                                      <>
                                        Routing: •••• {paymentMethod.routingNumberLast4}
                                      </>
                                    ) : (
                                      <>
                                        Expires: {formatExpiry(paymentMethod.expiryMonth, paymentMethod.expiryYear)}
                                      </>
                                    )}
                                  </div>
                                  {paymentMethod.nickName && (
                                    <div className="text-xs text-gray-500">
                                      {paymentMethod.nickName}
                                    </div>
                                  )}
                                  <div className="mt-1">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${providerColor}`}>
                                      Provider: {providerBadge}
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    Added: {paymentMethod.createdAt ? formatSafeDate(paymentMethod.createdAt) : 'N/A'}
                                  </div>
                                </div>
                              </div>
                              
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => handleDeletePaymentMethod(paymentMethod)}
                                className="flex items-center gap-1"
                              >
                                <Trash2 className="h-4 w-4" />
                                Delete
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="py-8 text-center text-gray-500">
                        No payment methods found for this customer
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Footer */}
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Payment Method</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to delete this payment method? This action cannot be undone.
            </p>
            {paymentMethodToDelete && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">
                  {paymentMethodToDelete.type === 'ach' ? (
                    <>
                      {paymentMethodToDelete.accountType || 'Bank Account'} •••• {paymentMethodToDelete.last4}
                    </>
                  ) : (
                    <>
                      {getCardBrandDetails(paymentMethodToDelete.cardType).name} •••• {paymentMethodToDelete.last4}
                    </>
                  )}
                </p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeletePaymentMethod}
              disabled={deletePaymentMethodMutation.isPending}
            >
              {deletePaymentMethodMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

CustomerProfileModal.displayName = 'CustomerProfileModal';

export { CustomerProfileModal }; 
